import { defineStore } from 'pinia'
import {
  LAST_SCORES_KEY,
  LAST_THEME_KEY,
  readScoped,
  writeScoped,
  removeScoped,
  dropLegacyKey
} from '@/utils/userStorage'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    isAuthenticated: false,
    // True once checkAuthentication() has resolved at least once (used by the router guard)
    authChecked: false,
    // Whether public signup is open (read from a meta tag set by the backend)
    signupsEnabled: document.querySelector('meta[name="signups-enabled"]')?.getAttribute('content') === 'true',
    // Best scores from backend, keyed by numeric themeId
    scores: {},
    // Last scores by theme+language, persisted locally and scoped to the account.
    // Boots on the guest scope; applyLocalScope() rebinds it once the session is known.
    lastScores: readScoped(LAST_SCORES_KEY, null)
  }),

  getters: {
    // Returns a combined view for a given theme & language
    getUserScore: (state) => (themeId, language) => {
      const lang = language || 'ja'
      // Chercher d'abord avec la cle composite theme_langue, sinon fallback sur l'ancien format
      const compositeKey = `${themeId}_${lang}`
      const best = state.scores[compositeKey] || state.scores[themeId] || { best_score: 0, best_percentage: 0 }
      const lastKey = `${themeId}_${lang}`
      const localLast = state.lastScores[lastKey] || {}
      // The server now returns the last score too; it wins over the local cache,
      // which is per-browser and was reset when it got scoped per account.
      return { last_score: 0, last_percentage: 0, ...localLast, ...best }
    }
  },

  actions: {
    // Rebind every per-account localStorage bucket to the user currently signed in
    // (or to the guest scope after a logout). Must run on every session change:
    // without it, the next account on this browser inherits the previous one's
    // last scores and weak words.
    async applyLocalScope() {
      const userId = this.user?.id || null

      // Pre-scoping keys. Weak words are adopted by the account that signs in
      // (see flashcard store); the other two are dropped, a single session rebuilds them.
      dropLegacyKey(LAST_SCORES_KEY)
      dropLegacyKey(LAST_THEME_KEY)

      this.lastScores = readScoped(LAST_SCORES_KEY, userId)

      const { useFlashcardStore } = await import('./flashcard')
      useFlashcardStore().loadUnknownWords(userId)
    },

    async checkAuthentication() {
      try {
        const response = await fetch('/api/auth/current_user', {
          credentials: 'include'
        })

        if (response.ok) {
          const userData = await response.json()
          this.user = userData
          this.isAuthenticated = true
          await this.loadScores()
        } else {
          this.user = null
          this.isAuthenticated = false
        }
      } catch (error) {
        console.error('Error checking authentication:', error)
        this.user = null
        this.isAuthenticated = false
      } finally {
        await this.applyLocalScope()
        this.authChecked = true
      }
    },

    async login(pseudo, password = null) {
      try {
        const response = await fetch('/api/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          credentials: 'include',
          body: JSON.stringify({ pseudo, password })
        })

        const data = await response.json()

        if (response.ok) {
          if (data.success) {
            this.user = data.user
            this.isAuthenticated = true
            await this.applyLocalScope()
            await this.loadScores()
            return { success: true }
          } else if (data.password_required) {
            return { success: false, password_required: true }
          } else {
            return { success: false, error: data.error }
          }
        } else {
          return { success: false, error: data.error || 'Erreur de connexion' }
        }
      } catch (error) {
        console.error('Login error:', error)
        return { success: false, error: 'Erreur de connexion' }
      }
    },

    async signup(payload) {
      try {
        const response = await fetch('/api/auth/signup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          credentials: 'include',
          body: JSON.stringify(payload)
        })

        const data = await response.json()

        if (response.ok && data.success) {
          this.user = data.user
          this.isAuthenticated = true
          await this.applyLocalScope()
          await this.loadScores()
          return { success: true }
        } else {
          return { success: false, error: data.error }
        }
      } catch (error) {
        console.error('Signup error:', error)
        return { success: false, error: 'Erreur lors de la création du compte' }
      }
    },

    async forgotPassword(email) {
      try {
        const response = await fetch('/api/auth/forgot_password', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          credentials: 'include',
          body: JSON.stringify({ email })
        })

        const data = await response.json()
        return { success: response.ok && data.success }
      } catch (error) {
        console.error('Forgot password error:', error)
        return { success: false, error: 'Erreur lors de la demande' }
      }
    },

    async resetPassword({ token, password, password_confirmation }) {
      try {
        const response = await fetch('/api/auth/reset_password', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          credentials: 'include',
          body: JSON.stringify({ token, password, password_confirmation })
        })

        const data = await response.json()

        if (response.ok && data.success) {
          this.user = data.user
          this.isAuthenticated = true
          await this.applyLocalScope()
          await this.loadScores()
          return { success: true }
        } else {
          return { success: false, error: data.error }
        }
      } catch (error) {
        console.error('Reset password error:', error)
        return { success: false, error: 'Erreur lors de la réinitialisation' }
      }
    },

    async updateProfile(profileData) {
      try {
        const response = await fetch('/api/auth/update_profile', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          credentials: 'include',
          body: JSON.stringify(profileData)
        })

        const data = await response.json()

        if (response.ok && data.success) {
          this.user = data.user
          return { success: true }
        } else {
          return { success: false, error: data.error }
        }
      } catch (error) {
        console.error('Update profile error:', error)
        return { success: false, error: 'Erreur lors de la mise à jour' }
      }
    },

    async deleteAccount(confirmation) {
      try {
        const response = await fetch('/api/auth/account', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          credentials: 'include',
          body: JSON.stringify({ confirmation })
        })

        const data = await response.json()

        if (response.ok && data.success) {
          // Drop this account's local buckets too, nothing will ever read them again
          const deletedUserId = this.user?.id || null
          removeScoped(LAST_SCORES_KEY, deletedUserId)
          removeScoped(LAST_THEME_KEY, deletedUserId)
          const { useFlashcardStore } = await import('./flashcard')
          useFlashcardStore().forgetUnknownWords(deletedUserId)

          this.user = null
          this.isAuthenticated = false
          this.scores = {}
          await this.applyLocalScope()
          return { success: true }
        }

        return { success: false, error: data.error || 'Erreur lors de la suppression du compte' }
      } catch (error) {
        console.error('Delete account error:', error)
        return { success: false, error: 'Erreur lors de la suppression du compte' }
      }
    },

    async logout() {
      try {
        const response = await fetch('/api/auth/logout', {
          method: 'DELETE',
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          credentials: 'include'
        })

        // Déconnecter localement même si la requête échoue
        this.user = null
        this.isAuthenticated = false
        this.scores = {}
        await this.applyLocalScope()

        if (!response.ok) {
          console.warn('Logout request failed, but user logged out locally')
        }
      } catch (error) {
        console.error('Logout error:', error)
        // Déconnecter localement même en cas d'erreur réseau
        this.user = null
        this.isAuthenticated = false
        this.scores = {}
        await this.applyLocalScope()
      }
    },

    async loadScores() {
      if (!this.isAuthenticated) return

      try {
        const response = await fetch('/api/auth/scores', {
          credentials: 'include'
        })

        if (response.ok) {
          this.scores = await response.json()
        }
      } catch (error) {
        console.error('Error loading scores:', error)
      }
    },

    async saveScore(themeId, score, totalItems, language) {
      if (!this.isAuthenticated) return

      try {
        const response = await fetch('/api/auth/save_score', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          credentials: 'include',
          body: JSON.stringify({
            score: {
              theme_id: themeId,
              score: score,
              total_items: totalItems,
              language: language
            }
          })
        })

        const data = await response.json()

        if (response.ok) {
          // Recharger les scores pour mettre à jour l'affichage
          await this.loadScores()
          // Mettre à jour le dernier score (par langue) côté client
          const key = `${themeId}_${language || 'ja'}`
          this.lastScores[key] = {
            last_score: score,
            last_percentage: Math.round((score / Math.max(totalItems || 1, 1)) * 100)
          }
          writeScoped(LAST_SCORES_KEY, this.user?.id, this.lastScores)
          return { success: true, data }
        } else {
          return { success: false, error: data.error }
        }
      } catch (error) {
        console.error('Error saving score:', error)
        return { success: false, error: 'Erreur lors de la sauvegarde' }
      }
    }
  }
})